import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { grey } from '@mui/material/colors';
import { SelectField, SwitchField } from 'components/form';
import makeStyles from '@mui/styles/makeStyles';
import { HelpOutline } from '@mui/icons-material';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Tooltip, Typography } from '@mui/material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { deskingActions, deskingSelectors } from '../../../model';
import { BuilderViewType, FinanceProgramFormValues } from '../../../types';

interface Props {
  isSupportBehaviorSetting: boolean;
  menuBuilderViewType: BuilderViewType;
}

export const ProposalSettings = ({ menuBuilderViewType, isSupportBehaviorSetting }: Props) => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const { control, setValue } = useFormContext<FinanceProgramFormValues>();

  const shouldRedirectToOCA = useWatch({
    control,
    name: 'displaySettings.shouldRedirectToOCA',
  });

  const lenderProfilesWithOca = useSelector(deskingSelectors.lenderProfilesWithOca);
  const lenderProfilesWithOcaOptions = useSelector(deskingSelectors.lenderProfilesWithOcaOptions);

  return (
    <Box className={classes.container}>
      <Box className="panel">
        <Typography component="span" variant="subtitle2">
          Proposal Settings
        </Typography>

        <Tooltip placement="top" arrow title="Control which elements of the menu appear for the customer">
          <HelpOutline fontSize="small" />
        </Tooltip>
      </Box>

      <Box className="form">
        <SwitchField
          control={control}
          name="displaySettings.showFinanceProgramDescription"
          label="Show Finance Program Description"
        />

        <RenderWithCondition condition={menuBuilderViewType === BuilderViewType.AftermarketMenu}>
          <>
            <SwitchField
              control={control}
              name="displaySettings.excludeEquipmentSales"
              label="Exclude Equipment Sales from Menu Calculations"
              extraAction={() => {
                dispatch(deskingActions.updateRecalculate());
              }}
            />

            <SwitchField control={control} name="displaySettings.productDetails" label="Show Product Details" />
            <SwitchField control={control} name="displaySettings.productPrice" label="Show Product Price" />
          </>
        </RenderWithCondition>

        <RenderWithCondition condition={menuBuilderViewType === BuilderViewType.Finance}>
          <>
            <SwitchField control={control} name="displaySettings.showBestOptions" label="Show Best Options" />
            <SwitchField control={control} name="displaySettings.showLenderLogo" label="Show Lender Logo" />
            <SwitchField control={control} name="displaySettings.showLenderName" label="Show Lender Name" />
            <SwitchField control={control} name="displaySettings.showInterestRate" label="Show Interest Rate" />
          </>
        </RenderWithCondition>

        <RenderWithCondition condition={isSupportBehaviorSetting}>
          <Box display="flex" gap={2}>
            <SwitchField
              control={control}
              name="displaySettings.shouldRedirectToOCA"
              label="Send Credit App"
              extraAction={() => setValue('displaySettings.lenderProfileId', '')}
            />
            <RenderWithCondition condition={shouldRedirectToOCA && !!lenderProfilesWithOca.length}>
              <SelectField
                label="Select Credit App"
                control={control}
                name="displaySettings.lenderProfileId"
                options={lenderProfilesWithOcaOptions}
                size="small"
                slotProps={{
                  select: {
                    displayEmpty: true,
                  },
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            </RenderWithCondition>
          </Box>
        </RenderWithCondition>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  container: {
    gap: '12px',
    display: 'flex',
    flexDirection: 'column',

    '& .panel': {
      gap: '0.5rem',
      padding: '8px',
      display: 'flex',
      borderRadius: '2px',
      alignItems: 'center',
      backgroundColor: grey['A100'],
    },

    '& .form': {
      display: 'flex',
      flexDirection: 'column',
    },
  },
});
